"use client"

import { useState, useEffect } from "react"
import { Card, Input, Space, Tag, Image, Collapse, Typography, List, Empty, Pagination } from "antd"
import { SearchOutlined } from "@ant-design/icons"
import { getProducts } from "../../services/ProductServices"
import { useAuth } from "../../contexts/authContext"

const { Panel } = Collapse
const { Text, Title } = Typography

const ProductsPage = () => {
  const [productsData, setProductsData] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [filteredProducts, setFilteredProducts] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10
  const { getToken } = useAuth()

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const token = await getToken()
        const data = await getProducts(token)
        setProductsData(data)
        setFilteredProducts(data)
      } catch (error) {
        console.error("Error al obtener productos:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchProducts()
  }, [getToken])

  // Función para filtrar productos basados en el término de búsqueda
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredProducts(productsData)
    } else {
      const lowercasedSearch = searchTerm.toLowerCase()
      const filtered = productsData.filter((product) => {
        return (
          // Buscar en nombre
          product.name?.toLowerCase().includes(lowercasedSearch) ||
          // Buscar en categoría
          product.category
            ?.toLowerCase()
            .includes(lowercasedSearch) ||
          // Buscar en ID
          product.id
            ?.toLowerCase()
            .includes(lowercasedSearch) ||
          // Buscar en materiales
          product.materials?.some((material) => material.toLowerCase().includes(lowercasedSearch)) ||
          // Buscar en certificaciones
          product.certifications?.some((cert) => cert.toLowerCase().includes(lowercasedSearch)) ||
          // Buscar en detalles
          product.details
            ?.toLowerCase()
            .includes(lowercasedSearch) ||
          // Buscar en uso
          product.use
            ?.toLowerCase()
            .includes(lowercasedSearch)
        )
      })
      setFilteredProducts(filtered)
    }

    // Resetear a la primera página cuando cambia la búsqueda
    setCurrentPage(1)
  }, [searchTerm, productsData])

  const handleSearch = (e) => {
    setSearchTerm(e.target.value)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  // Calcular productos para la página actual
  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    return filteredProducts.slice(startIndex, endIndex)
  }

  const currentPageData = getCurrentPageData()

  const renderProductDetails = (product) => {
    return (
      <div style={{ padding: "16px" }}>
        <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
          <div style={{ minWidth: "200px" }}>
            <Image
              src={product.images?.[0] || "/placeholder.svg"}
              alt={product.name}
              width={200}
              height={200}
              style={{ objectFit: "cover" }}
            />
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: "16px" }}>
              <Text strong>Categoría:</Text> {product.category}
            </div>

            <div style={{ marginBottom: "16px" }}>
              <Text strong>Uso:</Text> {product.use}
            </div>

            <div style={{ marginBottom: "16px" }}>
              <Text strong>Materiales:</Text> {product.materials?.join(", ")}
            </div>

            <div style={{ marginBottom: "16px" }}>
              <Text strong>Certificaciones:</Text> {product.certifications?.join(", ")}
            </div>

            <div style={{ marginBottom: "16px" }}>
              <Text strong>Detalles:</Text> {product.details}
            </div>
          </div>
        </div>

        <div style={{ marginTop: "24px" }}>
          <Title level={5}>Medidas y Precios</Title>
          {product.measures?.map((measure, index) => (
            <Card key={index} style={{ marginBottom: "16px" }}>
              <div style={{ marginBottom: "8px" }}>
                <Text strong>Medida:</Text> {measure.medida}
              </div>
              <div style={{ marginBottom: "8px" }}>
                <Text strong>Stock:</Text> {measure.stock}
              </div>
              <div>
                <Text strong>Precios:</Text>
                <List
                  size="small"
                  dataSource={(measure.precios || []).filter((price) => price.precioTotal !== undefined)}
                  renderItem={(price) => (
                    <List.Item>
                      <Text>{price.label}:</Text> ${price.precioTotal}
                      {price.valorUnitario ? ` (Precio unitario: $${price.valorUnitario})` : ""}
                    </List.Item>
                  )}
                />
              </div>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  return (
    <Card
      title="Gestión de Productos"
      extra={
        <Space>
          <Input
            placeholder="Buscar productos"
            prefix={<SearchOutlined />}
            style={{ width: 200 }}
            value={searchTerm}
            onChange={handleSearch}
            allowClear
          />
        </Space>
      }
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "24px" }}>Cargando productos...</div>
      ) : filteredProducts.length > 0 ? (
        <>
          <Collapse accordion>
            {currentPageData.map((product) => (
              <Panel
                header={
                  <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                    <Image
                      src={product.images?.[0] || "/placeholder.svg"}
                      alt={product.name}
                      width={40}
                      height={40}
                      style={{ objectFit: "cover" }}
                    />
                    <span style={{ fontWeight: "bold" }}>{product.name}</span>
                    <span>ID: {product.id}</span>
                    {product.measures?.[0]?.stock && (
                      <Tag color={product.measures[0].stock > 100 ? "green" : "orange"}>
                        {product.measures[0].stock > 100 ? "En Stock" : "Stock Bajo"}
                      </Tag>
                    )}
                  </div>
                }
                key={product.id}
              >
                {renderProductDetails(product)}
              </Panel>
            ))}
          </Collapse>

          {filteredProducts.length > pageSize && (
            <div style={{ marginTop: 16, textAlign: "right" }}>
              <Pagination
                current={currentPage}
                onChange={handlePageChange}
                total={filteredProducts.length}
                pageSize={pageSize}
                showSizeChanger={false}
                showTotal={(total) => `Total ${total} productos`}
              />
            </div>
          )}
        </>
      ) : (
        <Empty description="No se encontraron productos" />
      )}
    </Card>
  )
}

export default ProductsPage

